<script lang="ts">
	import { Text, Button, Stack, toast } from '@Shelly/shelly-design-system';
	import { get } from 'svelte/store';
	import { profile_data } from 'src/stores/account/user';
	import { delete_profile, save_profile } from 'src/helpers/profile_helpers';
	import { energyPriceApi } from 'src/components/PromotionalIntegrations/Verivox/api/verivoxApi';
	import {
		ENERGY_PRICE_CTA_TEXT_COLOR,
		energyPriceColorSchemes,
		type ColorSchemeKey,
	} from 'src/components/PromotionalIntegrations/Verivox/utils/colorSchemes';
	import {
		clearVerivoxSurfaceDismissal,
		getVerivoxDismissalEntry,
		isVerivoxSurfaceDismissed,
		type VerivoxDismissSurface,
	} from 'src/components/PromotionalIntegrations/Verivox/utils/verivoxDismissals';
	import {
		hasCompletedVerivoxLocation,
		shouldShowVerivoxPromoBanner,
	} from 'src/helpers/verivox_promo_helpers';
	import {
		hasVerivoxBackendFlag,
		isEnergyPriceFeatureEnabled,
	} from 'src/helpers/energy_price_helpers';
	import {
		setVerivoxDevForceEnable,
		verivoxDevForceEnable,
	} from 'src/stores/features/dev/verivox-dev-force-enable';
	import {
		customDashboardPromoSlotEnabled,
		energyDashboardPromoSlotEnabled,
		globalInlineBannersEnabled,
	} from 'src/stores/features/dev/dashboard-promo-slots';
	import PromotionsDevSection from './PromotionsDevSection.svelte';

	let energyPriceScheme: ColorSchemeKey = energyPriceColorSchemes.getCurrentScheme();

	function resolvePersona(
		_profile: typeof $profile_data,
		force: boolean,
		backend: boolean,
	): 'off' | 'eligible' | 'setup' | 'incomplete' {
		if (!backend && !force) return 'off';
		if (hasCompletedVerivoxLocation()) return 'setup';
		const integration = get(profile_data)?.de_users?.verivox_integration;
		if (integration && (integration.postalCode || integration.locationId)) {
			return 'incomplete';
		}
		if (backend || force) return 'eligible';
		return 'off';
	}

	function formatDismissPreview(
		surface: VerivoxDismissSurface,
		_profile: typeof $profile_data,
		_force: boolean,
	): string {
		if (!isEnergyPriceFeatureEnabled()) return 'hidden (feature off)';
		if (hasCompletedVerivoxLocation() && surface !== 'consumption') {
			return 'hidden (setup complete)';
		}
		const entry = getVerivoxDismissalEntry(surface);
		if (entry?.dismissed && entry.dismissalDate) {
			const until = new Date(entry.dismissalDate + 30 * 24 * 60 * 60 * 1000);
			return `dismissed until ${until.toLocaleDateString()}`;
		}
		if (isVerivoxSurfaceDismissed(surface)) return 'dismissed';
		if (shouldShowVerivoxPromoBanner(surface)) return 'visible';
		return 'hidden';
	}

	$: schemeKeys = Object.keys(energyPriceColorSchemes.schemes) as ColorSchemeKey[];
	$: featureEnabled = isEnergyPriceFeatureEnabled();
	$: backendFlag = hasVerivoxBackendFlag();
	$: forceEnable = $verivoxDevForceEnable;
	$: persona = resolvePersona($profile_data, forceEnable, backendFlag);
	$: notificationsDismissPreview = formatDismissPreview(
		'notifications',
		$profile_data,
		forceEnable,
	);
	$: energyDashDismissPreview = formatDismissPreview(
		'energyDashboard',
		$profile_data,
		forceEnable,
	);

	const handleEnergyPriceSchemeChange = (scheme: ColorSchemeKey) => {
		energyPriceScheme = scheme;
		energyPriceColorSchemes.setCurrentScheme(scheme);
		window.location.reload();
	};

	const setForceEnable = (enabled: boolean) => {
		setVerivoxDevForceEnable(enabled);
		toast.open({
			type: 'success',
			title: enabled ? 'Verivox force-enable ON' : 'Verivox force-enable OFF',
		});
	};

	const applyPersona = async (
		personaId: 'eligible' | 'setup' | 'incomplete',
	): Promise<void> => {
		setVerivoxDevForceEnable(true);

		let verivoxIntegration: Record<string, unknown> | undefined;

		if (personaId === 'eligible') {
			verivoxIntegration = undefined;
		} else if (personaId === 'setup') {
			verivoxIntegration = {
				postalCode: '10115',
				locationId: '7083',
				locationString: 'Berlin / Mitte',
				annualConsumption: 2500,
				consumptionPreference: 'average',
				dataConfirmationAccepted: true,
			};
		} else {
			verivoxIntegration = {
				postalCode: '10115',
			};
		}

		const existingDeUsers = get(profile_data)?.de_users || {};
		const deUsersData =
			verivoxIntegration === undefined
				? { ...existingDeUsers }
				: { ...existingDeUsers, verivox_integration: verivoxIntegration };

		if (verivoxIntegration === undefined) {
			delete deUsersData.verivox_integration;
		}

		const res = await save_profile('de_users', deUsersData);
		if (!res?.isok) {
			toast.open({ type: 'danger', title: 'Failed to apply Verivox persona' });
			return;
		}

		profile_data.update((current) => ({
			...current,
			de_users: deUsersData,
		}));

		toast.open({
			type: 'success',
			title: `Verivox persona: ${personaId}`,
		});
	};

	const removeVerivoxHomeData = async () => {
		try {
			await delete_profile('de_users');
			profile_data.update((current) => {
				const updated = { ...current };
				delete updated.de_users;
				return updated;
			});
			setVerivoxDevForceEnable(false);
			toast.open({
				type: 'success',
				title: 'Verivox home data removed (de_users deleted, force-enable off)',
			});
		} catch (error) {
			console.error('Failed to remove Verivox home data:', error);
			toast.open({ type: 'danger', title: 'Error removing Verivox home data' });
		}
	};

	const clearCachedPostalCode = async () => {
		try {
			await energyPriceApi.clearCachedLocation();
			toast.open({ type: 'success', title: 'Cached postal code cleared' });
		} catch (error) {
			console.error('Failed to clear cached postal code:', error);
			toast.open({ type: 'danger', title: 'Error clearing cached postal code' });
		}
	};

	const resetDismiss = async (surface: VerivoxDismissSurface, label: string) => {
		await clearVerivoxSurfaceDismissal(surface);
		toast.open({ type: 'success', title: `${label} dismissal cleared` });
	};
</script>

<Text styleType="h2" class="mb-m">Verivox integration</Text>

<Text class="mb-s text-bold">Force-enable UIs</Text>
<Stack gap="s" class="mb-s">
	<div class="d-flex flex-wrap gap-s">
		<Button
			variant={forceEnable ? 'primary' : 'secondary'}
			class="width-md-xl width-sm-full"
			on:click={() => setForceEnable(true)}
		>
			Force enable ON
		</Button>
		<Button
			variant={!forceEnable ? 'primary' : 'secondary'}
			class="width-md-xl width-sm-full"
			on:click={() => setForceEnable(false)}
		>
			Force enable OFF
		</Button>
	</div>
	<Text styleType="body-small" class="text-color-muted">
		Force enable: <b>{forceEnable ? 'on' : 'off'}</b>
		· Backend de_users: <b>{backendFlag ? 'present' : 'absent'}</b>
		· Feature effective: <b>{featureEnabled ? 'on' : 'off'}</b>
	</Text>
</Stack>

<hr class="mb-m mt-m" />

<Text class="mb-s text-bold">Personas</Text>
<Stack gap="s" class="mb-s">
	<div class="d-flex flex-wrap gap-s">
		<Button
			variant="secondary"
			class="width-md-xl width-sm-full"
			on:click={() => applyPersona('eligible')}
		>
			Eligible only
		</Button>
		<Button
			variant="secondary"
			class="width-md-xl width-sm-full"
			on:click={() => applyPersona('setup')}
		>
			Eligible + set up
		</Button>
		<Button
			variant="secondary"
			class="width-md-xl width-sm-full"
			on:click={() => applyPersona('incomplete')}
		>
			Incomplete setup
		</Button>
	</div>
	<Text styleType="body-small" class="text-color-muted">
		Current persona: <b>{persona}</b>
	</Text>
	<Button
		variant="danger-secondary"
		class="width-md-xl width-sm-full"
		on:click={removeVerivoxHomeData}
	>
		Remove de_users
	</Button>
</Stack>

<hr class="mb-m mt-m" />

<Text class="mb-s text-bold">Notifications dismiss</Text>
<Stack gap="s" class="mb-s">
	<Button
		variant="secondary"
		class="width-md-xl width-sm-full"
		on:click={() => resetDismiss('notifications', 'Notifications')}
	>
		Reset notifications banner dismiss
	</Button>
	<Text styleType="body-small" class="text-color-muted">
		State: <b>{notificationsDismissPreview}</b>
	</Text>
</Stack>

<hr class="mb-m mt-m" />

<Text class="mb-s text-bold">Energy dashboard slot dismiss</Text>
<Stack gap="s" class="mb-s">
	<Button
		variant="secondary"
		class="width-md-xl width-sm-full"
		on:click={() => resetDismiss('energyDashboard', 'Energy dashboard')}
	>
		Reset energy dashboard banner dismiss
	</Button>
	<Text styleType="body-small" class="text-color-muted">
		State: <b>{energyDashDismissPreview}</b>
	</Text>
</Stack>

<hr class="mb-m mt-m" />

<Text class="mb-s text-bold">Global inline banners</Text>
<Text styleType="body-small" class="text-color-muted mb-s">
	Controls the <b>GlobalInlineBanners</b> strip on the Rooms list (120px). Energy / Custom dash
	toggles below control sample/generic banners in the left-column <b>BannersWidget</b>.
	Default off — Verivox still appears for <b>de_users</b> (or Dev force-enable) without these toggles.
</Text>
<Stack gap="s" class="mb-s">
	<div class="d-flex flex-wrap gap-s">
		<Button
			variant={$globalInlineBannersEnabled ? 'primary' : 'secondary'}
			class="width-md-xl width-sm-full"
			on:click={() => globalInlineBannersEnabled.set(true)}
		>
			Global inline ON
		</Button>
		<Button
			variant={!$globalInlineBannersEnabled ? 'primary' : 'secondary'}
			class="width-md-xl width-sm-full"
			on:click={() => globalInlineBannersEnabled.set(false)}
		>
			Global inline OFF
		</Button>
	</div>
	<Text styleType="body-small" class="text-color-muted">
		Rooms strip: <b>{$globalInlineBannersEnabled ? 'on' : 'off'}</b> (default off)
	</Text>
	<div class="d-flex flex-wrap gap-s">
		<Button
			variant={$energyDashboardPromoSlotEnabled ? 'primary' : 'secondary'}
			class="width-md-xl width-sm-full"
			on:click={() => energyDashboardPromoSlotEnabled.set(true)}
		>
			Energy dash widget ON
		</Button>
		<Button
			variant={!$energyDashboardPromoSlotEnabled ? 'primary' : 'secondary'}
			class="width-md-xl width-sm-full"
			on:click={() => energyDashboardPromoSlotEnabled.set(false)}
		>
			Energy dash widget OFF
		</Button>
	</div>
	<div class="d-flex flex-wrap gap-s">
		<Button
			variant={$customDashboardPromoSlotEnabled ? 'primary' : 'secondary'}
			class="width-md-xl width-sm-full"
			on:click={() => customDashboardPromoSlotEnabled.set(true)}
		>
			Custom dash widget ON
		</Button>
		<Button
			variant={!$customDashboardPromoSlotEnabled ? 'primary' : 'secondary'}
			class="width-md-xl width-sm-full"
			on:click={() => customDashboardPromoSlotEnabled.set(false)}
		>
			Custom dash widget OFF
		</Button>
	</div>
	<Text styleType="body-small" class="text-color-muted">
		Energy widget: <b>{$energyDashboardPromoSlotEnabled ? 'on' : 'off'}</b> · Custom widget:
		<b>{$customDashboardPromoSlotEnabled ? 'on' : 'off'}</b> (default off)
	</Text>
	<Button
		variant="secondary"
		class="width-md-xl width-sm-full"
		on:click={() => resetDismiss('roomsList', 'Rooms list')}
	>
		Reset rooms list banner dismiss
	</Button>
</Stack>

<hr class="mb-m mt-m" />

<PromotionsDevSection />

<hr class="mb-m mt-m" />

<Text class="mb-s text-bold">Color scheme</Text>
<div class="energy-price-scheme-selector mb-m">
	{#each schemeKeys as scheme}
		<button
			class="energy-scheme-btn"
			class:active={energyPriceScheme === scheme}
			style="background: {energyPriceColorSchemes.schemes[
				scheme
			]}; --energy-price-cta-text: {ENERGY_PRICE_CTA_TEXT_COLOR}"
			on:click={() => handleEnergyPriceSchemeChange(scheme)}
			type="button"
		>
			{scheme.replace('scheme', '')}
		</button>
	{/each}
</div>

<Text class="mb-s text-bold">Clear data</Text>
<Stack gap="s">
	<Button
		variant="secondary"
		class="width-md-xl width-sm-full"
		on:click={clearCachedPostalCode}
	>
		Clear cached location
	</Button>
</Stack>

<style>
	.energy-price-scheme-selector {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-wrap: wrap;
	}

	.energy-scheme-btn {
		width: 40px;
		height: 40px;
		border-radius: 8px;
		border: 2px solid transparent;
		cursor: pointer;
		transition: all 0.2s ease;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 12px;
		color: #fff;
		font-weight: 600;
	}

	.energy-scheme-btn.active {
		border-color: #fff;
		box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.4);
	}
</style>
